import { useEffect, useState } from "react";
import { useApplicationToast } from "../webcontent/ApplicationToastProvider";
import {
  ExclamationTriangleIcon,
  PaperClipIcon,
} from "@heroicons/react/24/outline";
import { useConsentFormUrlQuery, useDownloadFormPDF } from "../api/ConsentApi";
import { usePermissionValidator } from "../api/UserPermissionValidators";
import { PersonConsentForm } from "../api/360Files";
import { formattedDate, resolveAndDownload } from "../utils";

type ConsentFormRowProps = {
  form: PersonConsentForm;
  /** "summary" matches Person Info link style on 360 summary page. */
  variant?: "sidebar" | "summary";
};

/** Single consent form row that matches document row style; click paperclip/text downloads PDF. */
const ConsentFormRow = ({ form, variant = "sidebar" }: ConsentFormRowProps) => {
  const { showLargeToast } = useApplicationToast();
  const { canDownloadPDF } = usePermissionValidator();
  const [startPreload, setStartPreload] = useState(false);
  const {
    isLoading: loadingPDF,
    mutate: downloadFormPDF,
    error: pdfError,
  } = useDownloadFormPDF();
  const { data: cachedUrl } = useConsentFormUrlQuery(
    form.signature_request_id,
    {
      enabled: startPreload,
    }
  );

  // Pre-load URL in background after the component finishes loading everything else
  useEffect(() => {
    const id =
      typeof requestIdleCallback !== "undefined"
        ? requestIdleCallback(() => setStartPreload(true), { timeout: 200 })
        : (setTimeout(() => setStartPreload(true), 0) as unknown as number);
    return () => {
      if (typeof requestIdleCallback !== "undefined") {
        cancelIdleCallback(id);
      } else {
        clearTimeout(id);
      }
    };
  }, []);

  useEffect(() => {
    if (pdfError) {
      showLargeToast?.({
        title: "Error!",
        message:
          "Couldn't download pdf please try again later or contact support.",
        icon: (
          <div className="bg-red-100 text-red-600 rounded-full p-2">
            <ExclamationTriangleIcon className="w-6" />
          </div>
        ),
      });
    }
  }, [pdfError, showLargeToast]);

  const handleDownload = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (cachedUrl) {
      resolveAndDownload(cachedUrl);
    } else {
      downloadFormPDF(form.signature_request_id);
    }
  };

  if (!canDownloadPDF) {
    return null;
  }

  const label =
    form.signed_at != null
      ? `Consent form (${formattedDate(form.signed_at)})`
      : "Consent form";

  const isSummary = variant === "summary";

  if (isSummary) {
    return (
      <button
        type="button"
        className="text-gray-500 overflow-hidden text-ellipsis whitespace-nowrap relative hover:underline pl-5 block mt-0.5 text-left w-full cursor-pointer"
        onClick={handleDownload}
        disabled={loadingPDF}
      >
        <span className="absolute top-1 left-0 text-black">
          <PaperClipIcon className="w-4 h-4" />
        </span>
        {label}
      </button>
    );
  }

  return (
    <div className="flex gap-2 items-center mt-0.5 group">
      <button
        type="button"
        className="flex gap-2 items-center flex-1 min-w-0 text-left cursor-pointer"
        onClick={handleDownload}
        disabled={loadingPDF}
      >
        <PaperClipIcon className="w-4 h-4 text-gray-500 flex-shrink-0" />
        <span className="truncate flex-1 text-sm font-semibold leading-5 text-gray-700">
          {label}
        </span>
      </button>
    </div>
  );
};

export default ConsentFormRow;
